/**
 * Closeout's quote panel, the repair-or-replace rule's face (#1958).
 *
 * The server decides once per failed device (repairOrReplaceRule.ts) and both
 * the customer's wording and the options read that answer. This shows the
 * office the same answer in one plain line, the facts it rests on (with where
 * each came from), and the one tap that settles anything unknown.
 *
 * Visual first (the office scans, it doesn't read): the colour and the icon
 * carry the meaning, and the words are short. No codes, no bell.
 */
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, CircleHelp, Hammer, Repeat, Scale, UserPlus, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export type RuleChoice = "replace" | "repair" | "pick_by_cost" | "check_first";
export type FactKey = "shutoffType" | "shutoffBrand" | "bodyMaterial";

export interface CloseoutRuleView {
    choice: RuleChoice;
    reason: string;
    shutoffWork: boolean;
    shutoffPhrase: string | null;
    facts: Array<{
        key: FactKey;
        label: string;
        value: string | null;
        valueLabel: string | null;
        sourceLabel: string | null;
        needsConfirm: boolean;
    }>;
    techSays: string | null;
    noReadingsCantFile: boolean;
}

/** The three taps, the same words the tech sees. */
export const FACT_CHOICES: Record<FactKey, Array<{ value: string; label: string }>> = {
    shutoffType: [
        { value: "osy", label: "Rising stem (OS&Y)" },
        { value: "nrs", label: "Non-rising (NRS)" },
        { value: "ball", label: "Ball" },
    ],
    shutoffBrand: [
        { value: "kennedy", label: "Kennedy" },
        { value: "wilkins", label: "Wilkins" },
        { value: "other", label: "Other" },
    ],
    bodyMaterial: [
        { value: "brass", label: "Brass" },
        { value: "stainless", label: "Stainless" },
        { value: "iron", label: "Iron" },
        { value: "other", label: "Other" },
    ],
};

const LOOK: Record<RuleChoice, { box: string; icon: typeof Repeat }> = {
    replace: {
        box: "border-violet-300 bg-violet-50 text-violet-900 dark:border-violet-700 dark:bg-violet-900/25 dark:text-violet-100",
        icon: Repeat,
    },
    repair: {
        box: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-100",
        icon: Hammer,
    },
    pick_by_cost: {
        box: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/25 dark:text-amber-100",
        icon: Scale,
    },
    check_first: {
        box: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/25 dark:text-amber-100",
        icon: CircleHelp,
    },
};

/** Worth showing: a shut-off is in play, the tech left a note, or the paper had nothing. */
export function ruleWorthShowing(rule: CloseoutRuleView | undefined | null): rule is CloseoutRuleView {
    return !!rule && (rule.shutoffWork || !!rule.techSays || rule.noReadingsCantFile);
}

export function RepairRuleStrip({
    jobId,
    assetId,
    rule,
    editable,
    onSaved,
}: {
    jobId: string;
    assetId: string;
    rule: CloseoutRuleView;
    editable: boolean;
    onSaved: () => void;
}) {
    const save = useMutation({
        mutationFn: (body: Partial<Record<FactKey, string>>) =>
            api.put(`/api/close-out/jobs/${jobId}/devices/${assetId}/facts`, body),
        onSuccess: () => {
            toast.success("Saved on the device. Rebuild this quote to use it.");
            onSaved();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save that."),
    });
    const look = LOOK[rule.choice];
    const Icon = look.icon;
    // Taps only when an unknown would change the answer (check first). A
    // decided device shows just the facts it rests on, so the office can
    // confirm a model's or a photo's answer. Over 4" the facts don't decide it.
    const facts = !rule.shutoffWork
        ? []
        : rule.choice === "check_first"
          ? rule.facts
          : rule.choice === "pick_by_cost"
            ? []
            : rule.facts.filter((f) => f.value);
    return (
        <div className="mt-1.5 space-y-1" data-testid="repair-rule-strip">
            {rule.noReadingsCantFile && (
                <div className="flex items-start gap-1.5 rounded-md border border-red-300 bg-red-50 px-2 py-1.5 text-[11.5px] font-semibold text-red-800 dark:border-red-800 dark:bg-red-900/25 dark:text-red-200">
                    <XCircle aria-hidden className="mt-px h-3.5 w-3.5 flex-none" />
                    No readings: can&apos;t file the city report from this fail.
                </div>
            )}
            {rule.techSays && (
                <div className="rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-[11.5px] font-semibold text-sky-900 dark:border-sky-800 dark:bg-sky-900/25 dark:text-sky-100">
                    {rule.techSays}
                </div>
            )}
            {rule.shutoffWork && (
                <div className={cn("rounded-md border px-2 py-1.5", look.box)}>
                    <div className="flex items-start gap-1.5 text-[11.5px] font-bold">
                        <Icon aria-hidden className="mt-px h-3.5 w-3.5 flex-none" />
                        <span className="min-w-0">{rule.reason}</span>
                    </div>
                    {facts.length > 0 && (
                        <div className="mt-1 space-y-1">
                            {facts.map((f) => (
                                <div key={f.key} className="flex flex-wrap items-center gap-1 text-[11px]">
                                    <span className="w-[92px] flex-none font-semibold opacity-80">{f.label}</span>
                                    {f.value ? (
                                        <>
                                            <span className="font-bold">{f.valueLabel}</span>
                                            {f.sourceLabel && <span className="opacity-70">({f.sourceLabel})</span>}
                                            {editable && f.needsConfirm && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-6 px-2 text-[10.5px]"
                                                    disabled={save.isPending}
                                                    onClick={() => save.mutate({ [f.key]: f.value! })}
                                                >
                                                    <CheckCircle2 className="mr-1 h-3 w-3" /> Confirm
                                                </Button>
                                            )}
                                        </>
                                    ) : editable ? (
                                        FACT_CHOICES[f.key].map((c) => (
                                            <Button
                                                key={c.value}
                                                size="sm"
                                                variant="outline"
                                                className="h-6 px-2 text-[10.5px]"
                                                disabled={save.isPending}
                                                onClick={() => save.mutate({ [f.key]: c.value })}
                                            >
                                                {c.label}
                                            </Button>
                                        ))
                                    ) : (
                                        <span className="opacity-70">not known</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export const NOT_PRICED_YET_PREFIX = "Not priced yet: ";

/** "Not priced yet: Repair #1 shut-off — 3" RPZ" → "#1 shut-off". */
export function notPricedLabel(description: string | null | undefined): string | null {
    const d = String(description ?? "");
    if (!d.startsWith(NOT_PRICED_YET_PREFIX)) return null;
    const rest = d.slice(NOT_PRICED_YET_PREFIX.length).split(" — ")[0]!.trim();
    // A $0 repair-kit line means nobody picked the kit (Renaissance FE0694).
    if (/^repair kit needed/i.test(rest)) return "pick the kit";
    return rest.replace(/^Repair\s+/, "") || "a needed piece";
}

/** The amber line: a needed piece that has no price, never an "only option". */
export function NotPricedYet({ labels }: { labels: string[] }) {
    if (labels.length === 0) return null;
    return (
        <div
            className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11.5px] font-bold text-amber-800 dark:border-amber-800 dark:bg-amber-900/25 dark:text-amber-200"
            data-testid="not-priced-yet"
        >
            <AlertTriangle aria-hidden className="mt-px h-3.5 w-3.5 flex-none" />
            <span>
                Not priced yet: {[...new Set(labels)].join(", ")}
                <span className="block text-[10.5px] font-semibold opacity-80">
                    The quote can&apos;t be approved or sent until it&apos;s priced or removed.
                </span>
            </span>
        </div>
    );
}

export interface TechAddedNeedsOk {
    headline: string;
    devices: Array<{ linkId: string; assetId: string; device: string }>;
}

/** "1 device the tech added needs your OK" — OK or Remove, one tap each. */
export function TechAddedNeedsOkBanner({
    jobId,
    data,
    onSaved,
}: {
    jobId: string;
    data: TechAddedNeedsOk;
    onSaved: () => void;
}) {
    const decide = useMutation({
        mutationFn: (body: { assetId: string; decision: "approved" | "declined" }) =>
            api.post(`/api/close-out/jobs/${jobId}/device-decision`, body),
        onSuccess: (_r, v) => {
            toast.success(v.decision === "approved" ? "OK'd. It stays on the quote." : "Removed from the quote.");
            onSaved();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save that."),
    });
    return (
        <div
            className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 dark:border-amber-800 dark:bg-amber-900/20"
            data-testid="tech-added-needs-ok"
        >
            <div className="flex items-center gap-1.5 text-[12px] font-extrabold text-amber-900 dark:text-amber-100">
                <UserPlus aria-hidden className="h-4 w-4" />
                {data.headline}
            </div>
            <p className="mt-0.5 text-[11px] text-amber-900/80 dark:text-amber-100/80">
                The tech added it on site. It&apos;s on the quote, and the quote can&apos;t go out until you decide.
            </p>
            <div className="mt-1.5 space-y-1">
                {data.devices.map((d) => (
                    <div key={d.assetId} className="flex flex-wrap items-center gap-1.5">
                        <span className="min-w-0 flex-1 text-[12px] font-semibold">{d.device}</span>
                        <Button
                            size="sm"
                            className="h-7 bg-emerald-600 px-2.5 text-[11px] text-white hover:bg-emerald-700"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ assetId: d.assetId, decision: "approved" })}
                        >
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> OK this device
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2.5 text-[11px]"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ assetId: d.assetId, decision: "declined" })}
                        >
                            Remove
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
}
